import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import type { IFAQ } from '@/api/endpoints/mainPages';
import { useDeleteFAQMutation, useMoveFAQMutation } from '@/api/endpoints/mainPages';
import { DeleteConfirmationModal, DraggableTable } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateFAQModal } from './components/Modals/CreateFAQModal';
import { EditFAQModal } from './components/Modals/EditFAQModal';
import { getColumnsData } from './helpers';
import styles from './MainFAQsPage.module.scss';
import type { IFAQItemData } from './types';

interface IProp {
  FAQsData: IFAQ[] | [];
  areFAQsFetching: boolean;
  pageName: string;
  pageId: number;
}

export const MainFAQsPageComponent = ({ FAQsData, areFAQsFetching, pageName, pageId }: IProp) => {
  const navigate = useNavigate();

  const [deleteFAQ, { isLoading: isFAQDeleting }] = useDeleteFAQMutation();
  const [moveFAQ] = useMoveFAQMutation();

  const sendApiRequest = useApiRequest();

  const [isCreateFAQModalOpened, setIsCreateFAQModalOpened] = useState(false);
  const [isEditFAQModalOpened, setIsEditFAQModalOpened] = useState(false);
  const [isDeleteFAQModalOpened, setIsDeleteFAQModalOpened] = useState(false);

  const openCreateFAQModal = useCallback(() => setIsCreateFAQModalOpened(true), []);
  const closeCreateFAQModal = useCallback(() => setIsCreateFAQModalOpened(false), []);

  const openEditFAQModal = useCallback(() => setIsEditFAQModalOpened(true), []);
  const closeEditFAQModal = useCallback(() => setIsEditFAQModalOpened(false), []);

  const openDeleteFAQModal = useCallback(() => setIsDeleteFAQModalOpened(true), []);
  const closeDeleteFAQModal = useCallback(() => setIsDeleteFAQModalOpened(false), []);

  const [tableData, setTableData] = useState<IFAQ[]>([]);
  const [currentFAQItem, setCurrentFAQItem] = useState<null | IFAQItemData>(null);

  const setItemToOperate = useCallback((data: IFAQItemData | null) => setCurrentFAQItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const columns = useMemo(() => getColumnsData({ setItemToOperate, openEditFAQModal, openDeleteFAQModal }), []);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      sendApiRequest({
        successMessage: 'Порядок вопросов успешно изменён',
        apiRequestCallback: () =>
          moveFAQ({
            mainPageId: pageId,
            FAQId: tableData[activeIndex].id,
            position: Number(overIndex) + 1,
          }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  useEffect(() => {
    setTableData(FAQsData);
  }, [FAQsData]);

  const handleDeleteFAQ = () => {
    if (currentFAQItem) {
      sendApiRequest({
        successMessage: 'Вопрос успешно удалён',
        apiRequestCallback: () =>
          deleteFAQ({
            mainPageId: pageId,
            FAQId: currentFAQItem.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteFAQModal),
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'FAQ' },
        ]}
      />
      <Button
        className={styles['create-button']}
        size="large"
        type="primary"
        onClick={openCreateFAQModal}
        disabled={FAQsData.length >= 4}
      >
        Добавить вопрос
      </Button>
      <DraggableTable
        onDragEnd={onDragEnd}
        keysForSorting={tableData.map((i) => i.position)}
        columns={columns}
        rowKey="position"
        size="large"
        locale={TABLE_TRANSLATIONS}
        pagination={false}
        dataSource={tableData}
        loading={areFAQsFetching}
      />
      <Button
        className={styles.button}
        size="large"
        type="dashed"
        onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`)}
      >
        Назад
      </Button>
      {isDeleteFAQModalOpened && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteFAQModalOpened}
          description="Вы уверены, что хотите удалить данный вопрос?"
          title="Удаление вопроса"
          handleCancel={() => handleWithClearOperateItem(closeDeleteFAQModal)}
          handleOk={handleDeleteFAQ}
          itemToOperate={currentFAQItem}
          shouldDisableElement={isFAQDeleting}
        />
      )}
      {isCreateFAQModalOpened && (
        <CreateFAQModal
          pageId={pageId}
          isModalOpen={isCreateFAQModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeCreateFAQModal)}
        />
      )}
      {isEditFAQModalOpened && currentFAQItem && (
        <EditFAQModal
          pageId={pageId}
          isModalOpen={isEditFAQModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeEditFAQModal)}
          handleClearOperateItem={() => handleWithClearOperateItem()}
          itemToOperate={currentFAQItem}
        />
      )}
    </div>
  );
};
